/* eslint-disable no-confusing-arrow */

import { logger } from 'utils';
import { SILENCE, MUSIC } from 'constants/audio';
import { Music } from 'types';
import { Store } from 'stores/type';
import { AudioManagerClass } from './index';

// fade duration for visibility change
const VISIBILITY_FADE_DURATION = 0.4;

class MusicPlayer {
  private $store: Store;
  private base: AudioManagerClass;
  private context: AudioContext;
  private timer: number | null = null;

  private source: AudioBufferSourceNode | null = null;
  private startedAt = -1;
  private pausedAt = 0;

  constructor(base: AudioManagerClass, context: AudioContext, store: Store) {
    this.base = base;
    this.context = context;
    this.$store = store;

    this.initVisibilityListener();
    this.initMuteListener();
  }
  private initVisibilityListener() {
    this.$store.watch(
      (_state, getters) => getters.isGameActive,
      (isActive) => isActive ? this.onShow() : this.onHide(),
    );
  }
  private initMuteListener() {
    this.$store.watch(
      (_state, getters) => getters.playMusic,
      (shouldPlay) => {
        logger('music', 'mute music', !shouldPlay);
        if (this.isPlaying && !shouldPlay) {
          this.pause();
          return;
        }

        if (!this.isPlaying && shouldPlay && this.current) {
          this.play(this.current);
        }
      },
    );
  }

  public play(name: string) {
    const sound = this.base.sounds.get(name) as Music;
    if (!this.isValid(sound)) return;

    this.$store.dispatch('musicPlay', name);
    logger('music', 'play track', sound);

    // if silence then just wait for it to last and call onEnded
    if (this.current === SILENCE) {
      window.setTimeout(() => {
        this.$store.dispatch('musicStop');
        this.onEnded();
      }, this.currentDuration * 1000);
      return;
    }

    this.playSource(sound);
    if (this.currentDuration !== 0) {
      this.clearTimer();
      this.timer = window.setTimeout(
        this.durationReached,
        (this.currentDuration - this.pausedAt) * 1000,
      );
    }

    if (sound.fadeIn) {
      this.volume.fadeInMusic(sound.fadeIn, sound.volume);
    } else {
      this.volume.unmute(MUSIC, sound.volume);
    }
  }
  private isValid(sound: Music | null) {
    if (!sound) return false;
    if (this.isPlaying || this.isHidden) return false;

    // if muted then set in state to know which track should be played
    if (!this.volume.canPlay(MUSIC) || !this.$store.getters.isGameActive) {
      this.$store.dispatch('musicSet', sound.name);
      return false;
    }

    // load if not available
    if (!sound.buffer) {
      this.base.sounds.load(sound)
        .then(() => this.play(sound.name))
        // if error occured during load or decoding then don't retry
        .catch(() => null);

      return false;
    }

    return true;
  }
  private playSource(sound: Music) {
    if (!sound.buffer) return;

    const source = this.context.createBufferSource();
    source.buffer = sound.buffer;
    source.loop = sound.loop;
    source.connect(this.volume.music);

    const offset = this.pausedAt;
    source.start(0, offset);
    source.onended = this.onEnded;

    this.source = source;
    this.startedAt = this.context.currentTime - this.pausedAt;
    this.pausedAt = 0;
  }

  public pause() {
    const elapsed = this.context.currentTime - this.startedAt;
    this.pausedAt = elapsed;

    this.stop();
    this.$store.dispatch('musicPause');
  }

  public stop(withListener = false) {
    if (this.current === null || this.source === null) return;

    if (!this.source.stop) {
      this.source.stop = (this.source as any).noteOff;
    }

    this.clearTimer();
    // if stopped with listener then track finished
    // if without listener then paused
    if (!withListener) {
      this.source.onended = null;
    } else {
      this.$store.dispatch('musicStop');
    }

    logger('music', 'stop track', this.current);

    this.source.stop(0);
    this.source = null;
    this.startedAt = -1;
  }

  private onEnded = () => {
    this.base.onTrackFinished();
  }

  public async onShow() {
    if (!this.currentSound || this.isPlaying) return;

    await this.context.resume();
    logger('music', 'start on show', this.current);
    this.play(this.current!);
    if (this.currentSound && !this.currentSound.fadeIn) {
      this.volume.fadeInMusic(VISIBILITY_FADE_DURATION, this.currentSound.volume);
    }
  }
  public async onHide() {
    if (!this.isPlaying) return;

    logger('music', 'stop on hide', this.current);
    await this.volume.fadeOutMusic(VISIBILITY_FADE_DURATION);
    await this.pause();
    this.context.suspend();
    this.clearTimer();
  }

  private clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
    }

    this.timer = null;
  }

  private durationReached = () => {
    const SAFETY_OFFSET = 0.5;
    const elapsed = (this.context.currentTime - this.startedAt) + SAFETY_OFFSET;
    if (elapsed < this.currentDuration) return;

    if (this.currentSound && this.currentSound.fadeOut) {
      this.volume.fadeOutMusic(this.currentSound.fadeOut)
        .then(() => this.stop(true));
      return;
    }

    this.stop(true);
  }

  get volume() {
    return this.base.volume;
  }
  get isPlaying() {
    return this.$store.getters.isMusicPlaying;
  }
  get current() {
    return this.$store.getters.currentMusic;
  }
  get currentSound() {
    if (!this.current) {
      return null;
    }

    return this.base.sounds.get(this.current) as Music;
  }
  get currentDuration() {
    if (!this.current || !this.currentSound) {
      return 0;
    }

    const sound = this.currentSound;
    const duration = sound.duration;
    const fadeOut = sound.fadeOut ? sound.fadeOut : 0;

    return duration - fadeOut;
  }
  get isHidden() {
    return document.visibilityState === 'hidden' || document.hidden;
  }
}

export default MusicPlayer;
